import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { Calculator, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FIRE Calculator Component
const FIRECalculator = ({ language }) => {
  const [inputs, setInputs] = useState({
    currentAge: '',
    currentSavings: '',
    monthlyExpenses: '',
    monthlyIncome: '',
    investmentReturn: 7
  });
  const [results, setResults] = useState(null);

  const calculate = () => {
    const age = parseFloat(inputs.currentAge);
    const savings = parseFloat(inputs.currentSavings);
    const expenses = parseFloat(inputs.monthlyExpenses);
    const income = parseFloat(inputs.monthlyIncome);
    const returnRate = parseFloat(inputs.investmentReturn) / 100;

    const annualExpenses = expenses * 12;
    const fireNumber = annualExpenses * 25; // 4% rule
    const monthlySavings = income - expenses;
    const annualSavings = monthlySavings * 12;

    // Calculate years to FIRE
    let years = 0;
    let currentSavings = savings;
    
    if (monthlySavings <= 0) {
      setResults({ error: true });
      return;
    }

    while (currentSavings < fireNumber && years < 100) {
      currentSavings = currentSavings * (1 + returnRate) + annualSavings;
      years++;
    }

    const fireAge = age + years;
    const savingsRate = (monthlySavings / income) * 100;

    setResults({
      fireNumber: Math.round(fireNumber),
      yearsToFire: years,
      fireAge: Math.round(fireAge),
      monthlySavings: Math.round(monthlySavings),
      savingsRate: savingsRate.toFixed(1),
      error: false
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>{language === 'ro' ? 'Vârsta actuală' : 'Current Age'}</Label>
          <Input
            type="number"
            value={inputs.currentAge}
            onChange={(e) => setInputs({ ...inputs, currentAge: e.target.value })}
            placeholder="35"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Economii actuale (€)' : 'Current Savings (€)'}</Label>
          <Input
            type="number"
            value={inputs.currentSavings}
            onChange={(e) => setInputs({ ...inputs, currentSavings: e.target.value })}
            placeholder="10000"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Cheltuieli lunare (€)' : 'Monthly Expenses (€)'}</Label>
          <Input
            type="number"
            value={inputs.monthlyExpenses}
            onChange={(e) => setInputs({ ...inputs, monthlyExpenses: e.target.value })}
            placeholder="1500"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Venit lunar (€)' : 'Monthly Income (€)'}</Label>
          <Input
            type="number"
            value={inputs.monthlyIncome}
            onChange={(e) => setInputs({ ...inputs, monthlyIncome: e.target.value })}
            placeholder="2500"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Randament anual investiții (%)' : 'Annual Investment Return (%)'}</Label>
          <Input
            type="number"
            value={inputs.investmentReturn}
            onChange={(e) => setInputs({ ...inputs, investmentReturn: e.target.value })}
            placeholder="7"
          />
        </div>
      </div>
      <Button 
        onClick={calculate} 
        disabled={!inputs.currentAge || !inputs.currentSavings || !inputs.monthlyExpenses || !inputs.monthlyIncome || !inputs.investmentReturn}
        className="w-full bg-gradient-to-r from-blue-600 to-mint-600 hover:from-blue-700 hover:to-mint-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {language === 'ro' ? 'Calculează când devii liber financiar' : 'Calculate Your Financial Freedom'}
      </Button>

      {results && !results.error && (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-mint-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Numărul FIRE' : 'FIRE Number'}
            </div>
            <div className="text-3xl font-bold text-blue-600">€{results.fireNumber.toLocaleString()}</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-mint-50 to-blue-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Ani până la FIRE' : 'Years to FIRE'}
            </div>
            <div className="text-3xl font-bold text-mint-600">{results.yearsToFire}</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-mint-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Vârsta FIRE' : 'FIRE Age'}
            </div>
            <div className="text-3xl font-bold text-blue-600">{results.fireAge}</div>
          </div>
        </div>
      )}
      {results && results.error && (
        <div className="rounded-lg bg-red-50 p-4 text-center text-red-600">
          {language === 'ro' 
            ? 'Houston, avem o problemă! Cheltuielile tale sunt mai mari decât venitul. Trebuie să economisești ceva, boss.' 
            : 'Your expenses are higher than income! You need to save something monthly.'}
        </div>
      )}
    </div>
  );
};

// Side Hustle Planner Component
const SideHustlePlanner = ({ language }) => {
  const [inputs, setInputs] = useState({
    targetMonthlyIncome: '',
    pricePerService: '',
    hoursPerWeek: '',
    hourlyRate: ''
  });
  const [results, setResults] = useState(null);

  const calculate = () => {
    const target = parseFloat(inputs.targetMonthlyIncome);
    const price = parseFloat(inputs.pricePerService);
    const hours = parseFloat(inputs.hoursPerWeek);
    const rate = parseFloat(inputs.hourlyRate);

    const clientsNeeded = Math.ceil(target / price);
    const monthlyHours = hours * 4.33; // avg weeks per month
    const potentialIncome = rate * monthlyHours;
    const servicesPerMonth = Math.floor(monthlyHours / (hours / 4)); // assuming 4 services per week

    setResults({
      clientsNeeded,
      servicesPerMonth,
      potentialIncome: Math.round(potentialIncome),
      hoursPerMonth: Math.round(monthlyHours)
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>{language === 'ro' ? 'Venit țintă lunar (€)' : 'Target Monthly Income (€)'}</Label>
          <Input
            type="number"
            value={inputs.targetMonthlyIncome}
            onChange={(e) => setInputs({ ...inputs, targetMonthlyIncome: e.target.value })}
            placeholder="1000"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Preț per serviciu/produs (€)' : 'Price per Service/Product (€)'}</Label>
          <Input
            type="number"
            value={inputs.pricePerService}
            onChange={(e) => setInputs({ ...inputs, pricePerService: e.target.value })}
            placeholder="50"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Ore disponibile pe săptămână' : 'Hours Available per Week'}</Label>
          <Input
            type="number"
            value={inputs.hoursPerWeek}
            onChange={(e) => setInputs({ ...inputs, hoursPerWeek: e.target.value })}
            placeholder="10"
          />
        </div>
        <div>
          <Label>{language === 'ro' ? 'Rată orară (€/h)' : 'Hourly Rate (€/h)'}</Label>
          <Input
            type="number"
            value={inputs.hourlyRate}
            onChange={(e) => setInputs({ ...inputs, hourlyRate: e.target.value })}
            placeholder="25"
          />
        </div>
      </div>
      <Button 
        onClick={calculate} 
        disabled={!inputs.targetMonthlyIncome || !inputs.pricePerService || !inputs.hoursPerWeek || !inputs.hourlyRate}
        className="w-full bg-gradient-to-r from-mint-600 to-blue-600 hover:from-mint-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {language === 'ro' ? 'Calculează Planul Tău' : 'Calculate Your Plan'}
      </Button>

      {results && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-gradient-to-br from-mint-50 to-blue-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Clienți necesari' : 'Clients Needed'}
            </div>
            <div className="text-3xl font-bold text-mint-600">{results.clientsNeeded}</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-mint-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Servicii/lună' : 'Services/Month'}
            </div>
            <div className="text-3xl font-bold text-blue-600">{results.servicesPerMonth}</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-mint-50 to-blue-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Venit potențial' : 'Potential Income'}
            </div>
            <div className="text-3xl font-bold text-mint-600">€{results.potentialIncome}</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-mint-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Ore/lună' : 'Hours/Month'}
            </div>
            <div className="text-3xl font-bold text-blue-600">{results.hoursPerMonth}h</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Debt Paydown Calculator Component
const DebtPaydownCalculator = ({ language }) => {
  const [debts, setDebts] = useState([
    { name: '', balance: '', rate: '', minPayment: '' }
  ]);
  const [extraPayment, setExtraPayment] = useState('');
  const [results, setResults] = useState(null);

  const addDebt = () => {
    setDebts([...debts, { name: '', balance: '', rate: '', minPayment: '' }]);
  };

  const updateDebt = (index, field, value) => {
    const newDebts = [...debts];
    newDebts[index][field] = value;
    setDebts(newDebts);
  };

  const removeDebt = (index) => {
    setDebts(debts.filter((_, i) => i !== index));
  };

  const calculate = () => {
    const validDebts = debts
      .filter(d => d.balance && d.rate && d.minPayment)
      .map(d => ({
        name: d.name || 'Datorie',
        balance: parseFloat(d.balance),
        rate: parseFloat(d.rate) / 100 / 12,
        minPayment: parseFloat(d.minPayment)
      }))
      .sort((a, b) => a.balance - b.balance); // Snowball method: smallest first

    if (validDebts.length === 0) return;

    const extra = parseFloat(extraPayment) || 0;
    let totalPaid = 0;
    let months = 0;
    const payoffSchedule = [];

    let activeDebts = [...validDebts];

    while (activeDebts.length > 0 && months < 600) {
      months++;
      let availableExtra = extra;

      activeDebts = activeDebts.map(debt => {
        const interest = debt.balance * debt.rate;
        let payment = debt.minPayment + (activeDebts.indexOf(debt) === 0 ? availableExtra : 0);
        
        if (payment > debt.balance + interest) {
          payment = debt.balance + interest;
          availableExtra = debt.minPayment + availableExtra - payment;
        } else {
          availableExtra = 0;
        }

        totalPaid += payment;
        const newBalance = Math.max(0, debt.balance + interest - payment);

        return { ...debt, balance: newBalance };
      }).filter(debt => debt.balance > 0.01);
    }

    const years = (months / 12).toFixed(1);
    const totalDebt = validDebts.reduce((sum, d) => sum + d.balance, 0);

    setResults({
      months,
      years,
      totalPaid: Math.round(totalPaid),
      totalInterest: Math.round(totalPaid - totalDebt),
      totalDebt: Math.round(totalDebt)
    });
  };

  return (
    <div className="space-y-6">
      {debts.map((debt, index) => (
        <div key={index} className="rounded-lg border-2 border-blue-200 bg-blue-50/50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-semibold text-slate-900">
              {language === 'ro' ? `Datorie ${index + 1}` : `Debt ${index + 1}`}
            </h4>
            {debts.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeDebt(index)} className="text-red-600">
                {language === 'ro' ? 'Șterge' : 'Remove'}
              </Button>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            <Input
              placeholder={language === 'ro' ? 'Nume (opțional)' : 'Name (optional)'}
              value={debt.name}
              onChange={(e) => updateDebt(index, 'name', e.target.value)}
            />
            <Input
              type="number"
              placeholder={language === 'ro' ? 'Sumă (€)' : 'Balance (€)'}
              value={debt.balance}
              onChange={(e) => updateDebt(index, 'balance', e.target.value)}
            />
            <Input
              type="number"
              placeholder={language === 'ro' ? 'Dobândă (%)' : 'Rate (%)'}
              value={debt.rate}
              onChange={(e) => updateDebt(index, 'rate', e.target.value)}
            />
            <Input
              type="number"
              placeholder={language === 'ro' ? 'Plată min (€)' : 'Min Payment (€)'}
              value={debt.minPayment}
              onChange={(e) => updateDebt(index, 'minPayment', e.target.value)}
            />
          </div>
        </div>
      ))}

      <Button onClick={addDebt} variant="outline" className="w-full">
        {language === 'ro' ? '+ Adaugă încă o datorie' : '+ Add Another Debt'}
      </Button>

      <div>
        <Label>{language === 'ro' ? 'Plată extra lunară (€)' : 'Extra Monthly Payment (€)'}</Label>
        <Input
          type="number"
          value={extraPayment}
          onChange={(e) => setExtraPayment(e.target.value)}
          placeholder="200"
        />
      </div>

      <Button 
        onClick={calculate} 
        disabled={debts.every(d => !d.balance || !d.rate || !d.minPayment)}
        className="w-full bg-gradient-to-r from-blue-600 to-mint-600 hover:from-blue-700 hover:to-mint-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {language === 'ro' ? 'Calculează Când Scapi de Datorii' : 'Calculate When You\'re Debt Free'}
      </Button>

      {results && (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-mint-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Timp până la plată' : 'Time to Payoff'}
            </div>
            <div className="text-3xl font-bold text-blue-600">{results.years} {language === 'ro' ? 'ani' : 'years'}</div>
            <div className="text-sm text-slate-600">({results.months} {language === 'ro' ? 'luni' : 'months'})</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-mint-50 to-blue-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Total plătit' : 'Total Paid'}
            </div>
            <div className="text-3xl font-bold text-mint-600">€{results.totalPaid.toLocaleString()}</div>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-blue-50 to-mint-50 p-6">
            <div className="mb-2 text-sm font-medium text-slate-600">
              {language === 'ro' ? 'Dobândă totală' : 'Total Interest'}
            </div>
            <div className="text-3xl font-bold text-red-600">€{results.totalInterest.toLocaleString()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const Tools = () => {
  const { t, language } = useLanguage();
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [monthlyExpenses, setMonthlyExpenses] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tools/savings-calculator`, {
        monthly_income: parseFloat(monthlyIncome),
        monthly_expenses: parseFloat(monthlyExpenses),
        language: language
      });
      setResult(response.data);
    } catch (error) {
      console.error('Failed to calculate:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container min-h-screen py-16 bg-gradient-to-br from-blue-50 via-mint-50 to-white">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-slate-900 sm:text-5xl" data-testid="tools-title">
            {t('tools.title')}
          </h1>
          <p className="text-lg text-slate-600">{t('tools.subtitle')}</p>
        </div>

        <div className="mx-auto max-w-4xl">
          <div className="grid gap-8 md:grid-cols-2">
            {/* Savings Rate Calculator */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Calculator className="mr-2 h-6 w-6 text-teal-600" />
                  {t('tools.savingsCalc')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCalculate} className="space-y-4">
                  <div>
                    <Label htmlFor="income">{t('tools.monthlyIncome')}</Label>
                    <Input
                      id="income"
                      type="number"
                      step="0.01"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      placeholder="2000"
                      required
                      data-testid="income-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="expenses">{t('tools.monthlyExpenses')}</Label>
                    <Input
                      id="expenses"
                      type="number"
                      step="0.01"
                      value={monthlyExpenses}
                      onChange={(e) => setMonthlyExpenses(e.target.value)}
                      placeholder="1500"
                      required
                      data-testid="expenses-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-mint-600 hover:from-blue-700 hover:to-mint-700"
                    disabled={loading || !monthlyIncome || !monthlyExpenses}
                    data-testid="calculate-button"
                  >
                    {language === 'ro' ? 'Calculează Rata de Economisire' : 'Calculate Savings Rate'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <Card className="border-0 bg-gradient-to-br from-teal-50 to-slate-50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center text-2xl">
                    <TrendingUp className="mr-2 h-6 w-6 text-teal-600" />
                    {t('tools.results')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6" data-testid="results-section">
                  <div>
                    <div className="mb-2 text-sm font-medium text-slate-600">
                      {language === 'ro' ? 'Rata de economisire' : 'Savings Rate'}
                    </div>
                    <div className="text-5xl font-bold text-teal-600">
                      {result.savings_rate}%
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 text-sm font-medium text-slate-600">
                      {language === 'ro' ? 'Economii lunare' : 'Monthly Savings'}
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      €{result.monthly_savings}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-4">
                    <p className="text-slate-700">{result.explanation}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* FIRE Calculator */}
          <div className="mt-12">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <TrendingUp className="mr-2 h-6 w-6 text-blue-600" />
                  {language === 'ro' ? 'Calculator FIRE (Independență Financiară)' : 'FIRE Calculator (Financial Independence)'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FIRECalculator language={language} />
              </CardContent>
            </Card>
          </div>

          {/* Side Hustle Planner */}
          <div className="mt-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <TrendingUp className="mr-2 h-6 w-6 text-mint-600" />
                  {language === 'ro' ? 'Planificator Side Hustle' : 'Side Hustle Planner'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SideHustlePlanner language={language} />
              </CardContent>
            </Card>
          </div>

          {/* Debt Paydown Calculator */}
          <div className="mt-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Calculator className="mr-2 h-6 w-6 text-blue-600" />
                  {language === 'ro' ? 'Calculator Plată Datorii (Snowball)' : 'Debt Paydown Calculator (Snowball)'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DebtPaydownCalculator language={language} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tools;